import { ApiErrorCode, createApiError } from '@/modules/common/api/errors';
import {
  mapIntegrationProviderToDTO,
  mapSelectedIntegrationFromDTO,
} from '@/modules/common/mappers/integration.mapper';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { EntityIntegration } from '@/modules/common/types/entity-integration';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { getIntegrationIcon } from '@/modules/common/utils/integration-icons';
import { CreateIntegrationInfoRequest } from '@/modules/projects/types/dto/request/create-project-request';

import { PatchTeamMemberIntegrationsRequest } from '../types/dto/request/patch-team-member-integrations-request';
import type { TeamMemberIntegration } from '../types/team-member-integration';

/**
 * Converts generic EntityIntegration to TeamMemberIntegration.
 * Maps `entities` to `teamMembers` and `selectedEntity` to `selectedValue`.
 * Sets allMembersLinked=true when the integration has no available members and no
 * selected value, meaning all external members are already linked to other team members.
 */
export const mapEntityIntegrationToTeamMemberIntegration = (
  entity: EntityIntegration
): TeamMemberIntegration => {
  const teamMembers = entity.entities;
  const selectedValue = entity.selectedEntity ?? null;
  const allMembersLinked = Array.isArray(teamMembers) && teamMembers.length === 0 && !selectedValue;

  return {
    id: entity.id,
    type: entity.type,
    icon: entity.icon,
    teamMembers,
    selectedValue,
    allMembersLinked,
  };
};

/**
 * Converts array of EntityIntegration to TeamMemberIntegration array.
 */
export const mapEntityIntegrationsToTeamMemberIntegrations = (
  entities: EntityIntegration[]
): TeamMemberIntegration[] => {
  return entities.map(mapEntityIntegrationToTeamMemberIntegration);
};

/**
 * Maps integration info responses from API to team member integrations.
 * Used when rendering integrations from team member details response.
 */
export const mapIntegrationsFromResponse = (
  rawIntegrations: IntegrationInfoResponse[]
): TeamMemberIntegration[] => {
  return rawIntegrations.map(integration => {
    const { type, selectedEntity } = mapSelectedIntegrationFromDTO(integration);

    const entityIntegration: EntityIntegration = {
      id: type,
      type,
      icon: getIntegrationIcon(type as IntegrationServices),
      entities: null,
      selectedEntity,
    };

    return mapEntityIntegrationToTeamMemberIntegration(entityIntegration);
  });
};

/**
 * Maps TeamMemberIntegration array to IntegrationInfoResponse array.
 * Converts domain integrations to raw API response format for passing to child components.
 */
export const mapTeamMemberIntegrationsToRawResponse = (
  integrations: TeamMemberIntegration[]
): IntegrationInfoResponse[] => {
  return integrations.map(integration => ({
    integrationProviderType: mapIntegrationProviderToDTO(integration.type as IntegrationServices),
    connectedIntegration: integration.selectedValue
      ? {
          id: Number(integration.selectedValue.id),
          name: integration.selectedValue.name,
        }
      : null,
  }));
};

/**
 * Maps TeamMemberIntegration array to CreateIntegrationInfoRequest array.
 * Only includes integrations that have a selected value.
 * Used when creating/updating team member integrations.
 */
export const mapTeamMemberIntegrationsToDTO = (
  integrations?: TeamMemberIntegration[]
): CreateIntegrationInfoRequest[] | undefined => {
  if (!integrations || integrations.length === 0) return undefined;

  return integrations
    .filter(integration => integration.selectedValue)
    .filter(integration => Number.isFinite(Number(integration.selectedValue?.id)))
    .map(integration => {
      const serviceType = integration.type as IntegrationServices;

      return {
        integrationProviderType: mapIntegrationProviderToDTO(serviceType),
        connectToLinkId: Number(integration.selectedValue?.id),
      };
    });
};

/**
 * Maps TeamMemberIntegration array to PatchTeamMemberIntegrationsRequest.
 * Validates team member ID and converts integrations to DTO format.
 */
export const mapTeamMemberIntegrationsToPatchRequest = (
  teamMemberId: string | number,
  integrations?: TeamMemberIntegration[]
): PatchTeamMemberIntegrationsRequest => {
  const id = typeof teamMemberId === 'number' ? teamMemberId : Number(teamMemberId);

  if (!Number.isFinite(id)) {
    const originalError = new Error(
      `Invalid teamMemberId provided to mapTeamMemberIntegrationsToPatchRequest: ${teamMemberId}`
    );
    throw createApiError(
      ApiErrorCode.TEAM_MEMBER_UPDATE_FAILED,
      originalError,
      'Invalid team member identifier for integrations update'
    );
  }

  const integrationsDTO = mapTeamMemberIntegrationsToDTO(integrations);

  return {
    entityId: id,
    integrations: {
      integrations: integrationsDTO || [],
    },
  };
};
